'use client';

import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import * as Sentry from '@sentry/nextjs';
import { getBrowserClient } from '@/lib/integrations/supabase/client';
import { useAuth } from '@/lib/integrations/supabase';
import { playNotificationSound, unlockAudio } from './sound-manager';
import { getNotifications, getUnreadNotificationCount, markNotificationRead, markAllNotificationsRead, deleteNotification, deleteAllNotifications } from '@/lib/features/data/inbox';
import { getPreferences, updatePreferences } from '@/lib/features/data/business';
import type { Notification } from '@/lib/integrations/supabase/types';

interface NotificationContextType {
  unreadCount: number;
  notifications: Notification[];
  isLoading: boolean;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  deleteAllNotifications: () => Promise<void>;
  isMuted: boolean;
  toggleMute: () => Promise<void>;
  refreshNotifications: () => Promise<void>;
  permission: NotificationPermission;
  requestPermission: () => Promise<NotificationPermission>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { user, businessId, role, isAdmin, effectiveBusinessId } = useAuth();
  const userId = user?.id ?? null;
  // For admin: use effectiveBusinessId (selected business), for others: use businessId
  const activeBusinessId = isAdmin ? effectiveBusinessId : businessId;
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');

  // Check notification permission on mount
  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setPermission(Notification.permission);
    }
  }, []);

  const requestPermission = useCallback(async (): Promise<NotificationPermission> => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      try {
        const result = await Notification.requestPermission();
        setPermission(result);
        return result;
      } catch (error) {
        Sentry.captureException(error);
        // Return current permission on error
        return Notification.permission;
      }
    }
    return 'default';
  }, []);
  useEffect(() => {
    unlockAudio();
  }, []);

  // Load mute preference (only for owner/manager/admin with selected business) and subscribe to real-time changes
  useEffect(() => {
    if (!activeBusinessId || (role !== 'owner' && role !== 'manager' && !isAdmin)) {
      setIsMuted(false); // Barbers don't have mute option
      return;
    }

    const loadMutePreference = async () => {
      try {
        const prefs = await getPreferences(activeBusinessId);
        setIsMuted(prefs?.mute_notification_sound ?? false);
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    loadMutePreference();

    // Subscribe to real-time changes in preferences
    const supabase = getBrowserClient();
    const channel = supabase
      .channel(`preferences-mute-${activeBusinessId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'preferences',
          filter: `business_id=eq.${activeBusinessId}`,
        },
        (payload) => {
          const updatedPrefs = payload.new as { mute_notification_sound?: boolean };
          if (updatedPrefs.mute_notification_sound !== undefined) {
            setIsMuted(updatedPrefs.mute_notification_sound);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeBusinessId, role, isAdmin]);

  // Clear notifications when user changes (logout or different user)
  useEffect(() => {
    if (!userId) {
      setNotifications([]);
      setUnreadCount(0);
      setInitialLoadDone(false);
    }
  }, [userId]);

  // Load initial notifications and count
  const loadNotifications = useCallback(async () => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    try {
      const [notifs, count] = await Promise.all([
        getNotifications(userId, 50),
        getUnreadNotificationCount(userId),
      ]);
      
      setNotifications(notifs);
      setUnreadCount(count);
      
      if (!initialLoadDone) {
        setInitialLoadDone(true);
      }
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setIsLoading(false);
    }
  }, [userId, initialLoadDone]);

  // Initial load
  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Refresh notifications when window regains focus
  useEffect(() => {
    const handleFocus = () => {
      if (userId) {
        loadNotifications();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [loadNotifications, userId]);

  // Real-time subscription
  useEffect(() => {
    if (!userId || !activeBusinessId) return;

    const supabase = getBrowserClient();

    // Subscribe to INSERT and UPDATE events, filtered by recipient_user_id
    // Note: Supabase Realtime only supports single-column filters, not AND conditions
    // RLS policies enforce business_id filtering at the database level
    const channel = supabase
      .channel(`notifications-${userId}-${activeBusinessId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_user_id=eq.${userId}`,
        },
        (payload) => {
          // Only process if this is a real-time event (not initial load)
          if (initialLoadDone) {
            const newNotification = payload.new as Notification;
            
            // Add to notifications list
            setNotifications((prev) => [newNotification, ...prev]);
            
            // Increment unread count only if the notification is unread
            if (newNotification.status === 'unread') {
              setUnreadCount((prev) => prev + 1);
            }

            // Send browser notification and play sound only if not muted
            // For owner: check mute preference
            // For barbers: always show notifications and play sound (no mute option for them)
            if (role === 'owner' || role === 'manager') {
              // Owner: only show notifications and play sound if not muted
              if (!isMuted) {
                // Send browser notification if enabled
                if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
                  try {
                    new Notification(newNotification.title, {
                      body: newNotification.body || undefined,
                      tag: newNotification.id, // Prevent duplicate notifications
                      icon: '/icons/icon-192x192.png', // Use PWA icon from correct path
                    });
                  } catch (e) {
                    Sentry.captureException(e);
                  }
                }
                playNotificationSound();
              }
              // If muted, don't show browser notification or play sound, but still add to inbox
            } else {
              // Barbers and other roles: always show notifications and play sound
              if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
                try {
                  new Notification(newNotification.title, {
                    body: newNotification.body || undefined,
                    tag: newNotification.id, // Prevent duplicate notifications
                    icon: '/icons/icon-192x192.png', // Use PWA icon from correct path
                  });
                } catch (e) {
                  Sentry.captureException(e);
                }
              }
              playNotificationSound();
            }
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_user_id=eq.${userId}`,
        },
        (payload) => {
          // Handle updates (e.g., when notification is marked as read)
          if (initialLoadDone) {
            const updatedNotification = payload.new as Notification;
            const oldNotification = payload.old as Notification;
            
            // Update the notification in the list
            setNotifications((prev) =>
              prev.map((n) =>
                n.id === updatedNotification.id ? updatedNotification : n
              )
            );
            
            // Update count if status changed from unread to read
            if (oldNotification.status === 'unread' && updatedNotification.status === 'read') {
              setUnreadCount((prev) => Math.max(0, prev - 1));
            } else if (oldNotification.status === 'read' && updatedNotification.status === 'unread') {
              setUnreadCount((prev) => prev + 1);
            }
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_user_id=eq.${userId}`,
        },
        (payload) => {
          // Handle deletions
          if (initialLoadDone) {
            const deletedNotification = payload.old as Notification;
            
            // Remove the notification from the list
            setNotifications((prev) =>
              prev.filter((n) => n.id !== deletedNotification.id)
            );
            
            // Update count if the deleted notification was unread
            if (deletedNotification.status === 'unread') {
              setUnreadCount((prev) => Math.max(0, prev - 1));
            }
          }
        }
      )
      .subscribe((status) => {
        Sentry.logger.debug('Notification subscription status changed', { status });
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, activeBusinessId, initialLoadDone, isMuted, role]);

  const markAsRead = useCallback(async (notificationId: string) => {
    // Optimistic update - update UI immediately
    const notification = notifications.find((n) => n.id === notificationId);
    const wasUnread = notification?.status === 'unread';
    
    // Update local state immediately for instant UI response
    setNotifications((prev) =>
      prev.map((n) =>
        n.id === notificationId ? { ...n, status: 'read' as const } : n
      )
    );
    
    if (wasUnread) {
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }
    
    // Process backend update in background (non-blocking)
    markNotificationRead(notificationId).catch((error) => {
      Sentry.captureException(error);
      // Revert on error
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, status: notification?.status || 'unread' as const } : n
        )
      );
      if (wasUnread) {
        setUnreadCount((prev) => prev + 1);
      }
    });
  }, [notifications]);

  const markAllAsRead = useCallback(async () => {
    if (!userId) return;
    
    // Optimistic update - update UI immediately
    const previousUnreadCount = unreadCount;
    const previousNotifications = [...notifications];
    
    // Update local state immediately for instant UI response
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, status: 'read' as const }))
    );
    setUnreadCount(0);
    
    // Process backend update in background (non-blocking)
    markAllNotificationsRead(userId).catch((error) => {
      Sentry.captureException(error);
      // Revert on error
      setNotifications(previousNotifications);
      setUnreadCount(previousUnreadCount);
    });
  }, [userId, notifications, unreadCount]);

  const handleDeleteNotification = useCallback(async (notificationId: string) => {
    // Optimistic update - update UI immediately
    const notification = notifications.find((n) => n.id === notificationId);
    const wasUnread = notification?.status === 'unread';
    const previousNotifications = [...notifications];
    
    // Update local state immediately for instant UI response
    const newNotifications = notifications.filter((n) => n.id !== notificationId);
    setNotifications(newNotifications);
    
    if (wasUnread) {
      setUnreadCount((prevCount) => Math.max(0, prevCount - 1));
    }
    
    // Process backend delete in background (non-blocking)
    deleteNotification(notificationId).catch((error) => {
      Sentry.captureException(error);
      // Revert on error
      setNotifications(previousNotifications);
      if (wasUnread) {
        setUnreadCount((prevCount) => prevCount + 1);
      }
    });
  }, [notifications]);

  const handleDeleteAllNotifications = useCallback(async () => {
    if (!activeBusinessId) return;

    // Optimistic update - update UI immediately
    const previousNotifications = [...notifications];
    const previousUnreadCount = unreadCount;

    // Clear local state immediately for instant UI response
    setNotifications([]);
    setUnreadCount(0);

    // Process backend delete in background (non-blocking)
    deleteAllNotifications(activeBusinessId).catch((error) => {
      Sentry.captureException(error);
      // Revert on error
      setNotifications(previousNotifications);
      setUnreadCount(previousUnreadCount);
    });
  }, [activeBusinessId, notifications, unreadCount]);

  const refreshNotifications = useCallback(async () => {
    await loadNotifications();
  }, [loadNotifications]);

  const toggleMute = useCallback(async () => {
    if (!activeBusinessId || (role !== 'owner' && role !== 'manager' && !isAdmin)) {
      return; // Only owner/manager/admin can toggle mute
    }

    try {
      const newMuteState = !isMuted;
      // Update local state optimistically
      setIsMuted(newMuteState);

      // Update backend
      await updatePreferences(activeBusinessId, { mute_notification_sound: newMuteState });
    } catch (error) {
      Sentry.captureException(error);
      // Revert on error by reloading preference
      try {
        const prefs = await getPreferences(activeBusinessId);
        setIsMuted(prefs?.mute_notification_sound ?? false);
      } catch (reloadError) {
        Sentry.captureException(reloadError);
        // If reload fails, revert to previous state
        setIsMuted(isMuted);
      }
      throw error;
    }
  }, [activeBusinessId, role, isMuted, isAdmin]);

  return (
    <NotificationContext.Provider
      value={{
        unreadCount,
        notifications,
        isLoading,
        markAsRead,
        markAllAsRead,
        deleteNotification: handleDeleteNotification,
        deleteAllNotifications: handleDeleteAllNotifications,
        isMuted,
        toggleMute,
        refreshNotifications,
        permission,
        requestPermission,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications(): NotificationContextType {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}

